import React, { useState, useRef } from 'react';
import { BackgroundSettings, BackgroundType, ParticleEffectType, PresetBackgroundId, UserProfile, CollectionImage, CollectionVideo } from '../types';
import { useUser } from '../contexts/UserContext';
import {
  presetBackgrounds,
  uploadBackgroundImage,
  uploadBackgroundVideo,
  deleteBackgroundFile,
  generateRandomGradient,
  validateGradientColor,
  applyThemeColors,
  getPresetThemeColors,
  generateThemeFromGradient,
} from '../services/backgroundService';
import { soundService } from '../services/soundService';
import { hapticService } from '../services/hapticService';
import { subscriptionService } from '../services/subscriptionService';

interface BackgroundCustomizerProps {
  currentSettings: BackgroundSettings;
  onSave: (settings: BackgroundSettings) => Promise<void>;
  onClose: () => void;
  userId: string;
  userProfile: UserProfile;
  onShowSubscription?: () => void;
  initialType?: BackgroundType; // Optional: which tab to open initially
  initialParticleEffect?: ParticleEffectType; // Optional: initial particle effect
}

const BackgroundCustomizer: React.FC<BackgroundCustomizerProps> = ({
  currentSettings,
  onSave,
  onClose,
  userId,
  userProfile,
  onShowSubscription,
  initialType,
  initialParticleEffect,
}) => {
  // Get demo mode from context
  const { isDemoMode } = useUser();
  const [settings, setSettings] = useState<BackgroundSettings>(() => {
    // If initialType is provided, override the current settings type
    if (initialType && initialType !== currentSettings.type) {
      const newSettings: BackgroundSettings = {
        ...currentSettings,
        type: initialType,
      };
      
      // Set defaults based on type
      if (initialType === 'preset') {
        newSettings.presetId = currentSettings.presetId || 'default_dark';
      } else if (initialType === 'gradient') {
        newSettings.gradient = currentSettings.gradient || { colors: ['#667eea', '#764ba2'], angle: 135 };
      } else if (initialType === 'solid') {
        newSettings.solidColor = currentSettings.solidColor || '#1a1a2e';
      }
      
      // Set initial particle effect if provided
      if (initialParticleEffect) {
        newSettings.particleEffect = initialParticleEffect;
      }
      
      return newSettings;
    }
    
    // Set initial particle effect if provided (even without changing type)
    if (initialParticleEffect && initialParticleEffect !== currentSettings.particleEffect) {
      return {
        ...currentSettings,
        particleEffect: initialParticleEffect,
      };
    }
    
    return currentSettings;
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const handleTypeChange = (type: BackgroundType) => {
    // Check for demo mode restrictions
    if (isDemoMode && (type === 'custom_image' || type === 'video')) {
      if (onShowSubscription) {
        onShowSubscription();
      }
      return;
    }
    
    soundService.buttonClick();
    hapticService.click();
    
    // Clear type-specific settings when changing type to prevent old data from showing
    // BUT preserve collections - they should persist across tab switches
    const cleanSettings: BackgroundSettings = {
      type,
      overlayOpacity: settings.overlayOpacity,
      blurIntensity: settings.blurIntensity,
      particleEffect: settings.particleEffect,
      particleIntensity: settings.particleIntensity,
      parallaxEnabled: settings.parallaxEnabled,
      reducedQuality: settings.reducedQuality,
      // ✅ PRESERVE COLLECTIONS across tab switches
      imageCollection: settings.imageCollection || [],
      videoCollection: settings.videoCollection || [],
    };
    
    // Set appropriate defaults based on type
    if (type === 'preset') {
      cleanSettings.presetId = 'default_dark';
    } else if (type === 'gradient') {
      cleanSettings.gradient = { colors: ['#667eea', '#764ba2'], angle: 135 };
    } else if (type === 'solid') {
      cleanSettings.solidColor = '#1a1a2e';
    }
    
    setSettings(cleanSettings);
    setError(null);
  };

  const handlePresetSelect = (presetId: PresetBackgroundId) => {
    const preset = presetBackgrounds[presetId];
    
    // Check if preset requires subscription
    if (preset.requiresSubscription) {
      const hasAccess = subscriptionService.hasSubscriptionAccess(userProfile.subscription, preset.tier || 's_rank');
      
      if (!hasAccess) {
        // Show subscription modal
        if (onShowSubscription) {
          onShowSubscription();
        }
        setError(`This background requires ${preset.tier === 's_rank' ? 'S-Rank' : 'E-Rank'} subscription`);
        hapticService.error();
        return;
      }
    }
    
    // Apply theme colors if available
    const themeColors = getPresetThemeColors(presetId);
    if (themeColors) {
      applyThemeColors(themeColors);
    }
    
    soundService.buttonClick();
    hapticService.click();
    
    // Clear custom image/video URLs when switching to preset
    setSettings({ 
      ...settings, 
      type: 'preset', 
      presetId,
      customImageUrl: undefined,
      customImageName: undefined,
      videoUrl: undefined,
      videoName: undefined,
    });
    setError(null);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if collection is full (max 6 images)
    const currentCollection = settings.imageCollection || [];
    if (currentCollection.length >= 6) {
      setError('Collection is full! Delete an image to upload a new one.');
      hapticService.error();
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const { url, fileName } = await uploadBackgroundImage(userId, file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Add to collection instead of directly applying
      const newCollectionImage: CollectionImage = {
        url,
        fileName,
        uploadedAt: Date.now(),
      };

      const updatedSettings = {
        ...settings,
        imageCollection: [...currentCollection, newCollectionImage],
      };

      setSettings(updatedSettings);

      // Auto-save collection to Firestore immediately
      await onSave(updatedSettings);

      hapticService.success();
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
      hapticService.error();
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleApplyCollectionImage = async (collectionImage: CollectionImage) => {
    soundService.buttonClick();
    hapticService.click();
    
    const updatedSettings = {
      ...settings,
      type: 'custom_image' as const,
      customImageUrl: collectionImage.url,
      customImageName: collectionImage.fileName,
    };

    setSettings(updatedSettings);

    // Auto-save to Firestore immediately so background updates
    try {
      await onSave(updatedSettings);
    } catch (err: any) {
      setError(err.message || 'Failed to apply image');
      hapticService.error();
    }
  };

  const handleDeleteCollectionImage = async (index: number) => {
    const currentCollection = settings.imageCollection || [];
    const imageToDelete = currentCollection[index];
    
    if (!imageToDelete) return;

    try {
      // Delete from Firebase Storage
      await deleteBackgroundFile(userId, imageToDelete.fileName);
      
      // Remove from collection
      const newCollection = currentCollection.filter((_, i) => i !== index);
      
      const updatedSettings = {
        ...settings,
        imageCollection: newCollection,
        // If the deleted image was the current background, clear it
        ...(settings.customImageName === imageToDelete.fileName ? {
          customImageUrl: undefined,
          customImageName: undefined,
        } : {}),
      };

      setSettings(updatedSettings);

      // Auto-save collection to Firestore immediately
      await onSave(updatedSettings);
      
      soundService.buttonClick();
      hapticService.success();
    } catch (err: any) {
      setError(err.message || 'Failed to delete image');
      hapticService.error();
    }
  };

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if collection is full (max 3 videos)
    const currentCollection = settings.videoCollection || [];
    if (currentCollection.length >= 3) {
      setError('Collection is full! Delete a video to upload a new one.');
      hapticService.error();
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 5, 90));
      }, 500);

      const { url, fileName } = await uploadBackgroundVideo(userId, file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Add to collection instead of directly applying
      const newCollectionVideo: CollectionVideo = {
        url,
        fileName,
        uploadedAt: Date.now(),
      };

      const updatedSettings = {
        ...settings,
        videoCollection: [...currentCollection, newCollectionVideo],
      };

      setSettings(updatedSettings);

      // Auto-save collection to Firestore immediately
      await onSave(updatedSettings);

      hapticService.success();
    } catch (err: any) {
      setError(err.message || 'Failed to upload video');
      hapticService.error();
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleApplyCollectionVideo = async (collectionVideo: CollectionVideo) => {
    soundService.buttonClick();
    hapticService.click();
    
    const updatedSettings = {
      ...settings,
      type: 'video' as const,
      videoUrl: collectionVideo.url,
      videoName: collectionVideo.fileName,
    };

    setSettings(updatedSettings);

    // Auto-save to Firestore immediately so background updates
    try {
      await onSave(updatedSettings);
    } catch (err: any) {
      setError(err.message || 'Failed to apply video');
      hapticService.error();
    }
  };

  const handleDeleteCollectionVideo = async (index: number) => {
    const currentCollection = settings.videoCollection || [];
    const videoToDelete = currentCollection[index];
    
    if (!videoToDelete) return;

    try {
      // Delete from Firebase Storage
      await deleteBackgroundFile(userId, videoToDelete.fileName);
      
      // Remove from collection
      const newCollection = currentCollection.filter((_, i) => i !== index);
      
      const updatedSettings = {
        ...settings,
        videoCollection: newCollection,
        // If the deleted video was the current background, clear it
        ...(settings.videoName === videoToDelete.fileName ? {
          videoUrl: undefined,
          videoName: undefined,
        } : {}),
      };

      setSettings(updatedSettings);

      // Auto-save collection to Firestore immediately
      await onSave(updatedSettings);
      
      soundService.buttonClick();
      hapticService.success();
    } catch (err: any) {
      setError(err.message || 'Failed to delete video');
      hapticService.error();
    }
  };

  const handleGradientColorChange = (index: number, color: string) => {
    if (!validateGradientColor(color)) return;

    const newColors = [...(settings.gradient?.colors || ['#667eea', '#764ba2'])];
    newColors[index] = color;

    // Apply theme colors live as user edits for real-time preview
    const themeColors = generateThemeFromGradient(newColors);
    applyThemeColors(themeColors);

    setSettings({
      ...settings,
      type: 'gradient',
      gradient: {
        colors: newColors,
        angle: settings.gradient?.angle || 135,
      },
    });
  };

  const handleAddGradientColor = () => {
    const currentColors = settings.gradient?.colors || ['#667eea', '#764ba2'];
    if (currentColors.length >= 5) return; // Max 5 colors

    setSettings({
      ...settings,
      type: 'gradient',
      gradient: {
        colors: [...currentColors, '#764ba2'],
        angle: settings.gradient?.angle || 135,
      },
    });
  };

  const handleRemoveGradientColor = (index: number) => {
    const currentColors = settings.gradient?.colors || ['#667eea', '#764ba2'];
    if (currentColors.length <= 2) return; // Min 2 colors

    const newColors = currentColors.filter((_, i) => i !== index);
    setSettings({
      ...settings,
      gradient: {
        colors: newColors,
        angle: settings.gradient?.angle || 135,
      },
    });
  };

  const handleRandomGradient = () => {
    soundService.buttonClick();
    hapticService.click();
    const randomGradient = generateRandomGradient();
    
    // Apply theme colors immediately for preview
    if (randomGradient.themeColors) {
      applyThemeColors(randomGradient.themeColors);
    }
    
    setSettings({
      ...settings,
      type: 'gradient',
      gradient: { colors: randomGradient.colors, angle: randomGradient.angle },
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // If user is saving a gradient (Mana Aura), apply theme colors
      if (settings.type === 'gradient' && settings.gradient) {
        const themeColors = generateThemeFromGradient(settings.gradient.colors);
        applyThemeColors(themeColors);
      }
      
      await onSave(settings);
      hapticService.success();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
      hapticService.error();
    } finally {
      setIsSaving(false);
    }
  };

  const particleEffects: { type: ParticleEffectType; label: string; icon: string }[] = [
    { type: 'none', label: 'None', icon: '⚪' },
    { type: 'snow', label: 'Snow', icon: '❄️' },
    { type: 'stars', label: 'Sparkles', icon: '✨' },
    { type: 'fireflies', label: 'Starlight', icon: '🌟' },
    { type: 'sakura', label: 'Petals', icon: '🌸' },
    { type: 'embers', label: 'Embers', icon: '🔥' },
  ];

  return (
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-50 p-4 modal-backdrop animate-fade-in">
      <div 
        className="bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-scale-in"
        style={{
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
        }}
      >
        <div className="overflow-y-auto max-h-[90vh]">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-purple-500/20 p-6 flex items-center justify-between z-10"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <span className="text-2xl">⚔️</span>
                Shadow Domain Customization
              </h2>
              <p className="text-sm text-gray-400 mt-1">Manifest your Hunter's personal realm</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-700/50 rounded-2xl transition-all border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60 relative group"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              <span className="text-xl text-gray-400">✕</span>
            </button>
          </div>

        <div className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div 
              className="bg-gradient-to-b from-red-950/40 via-slate-950/30 to-red-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 rounded-3xl p-6"
              style={{
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
              }}
            >
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Background Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">⚡ Realm Type</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { type: 'preset' as BackgroundType, label: 'Gate Realm', icon: '🌌' },
                { type: 'gradient' as BackgroundType, label: 'Mana Aura', icon: '💫' },
                { type: 'solid' as BackgroundType, label: 'Void', icon: '⬛' },
                // Demo mode: show locked options with signup prompts
                ...(isDemoMode ? [
                  { type: 'custom_image' as BackgroundType, label: 'Custom Gate', icon: '🎭', locked: true },
                  { type: 'video' as BackgroundType, label: 'Living Realm', icon: '🎬', locked: true },
                ] : [
                  { type: 'custom_image' as BackgroundType, label: 'Custom Gate', icon: '🎭' },
                  { type: 'video' as BackgroundType, label: 'Living Realm', icon: '🎬' },
                ])
              ].map((option) => (
                <button
                  key={option.type}
                  onClick={() => {
                    if (option.locked && isDemoMode) {
                      // Show signup prompt for locked features
                      if (onShowSubscription) {
                        onShowSubscription();
                      }
                      return;
                    }
                    handleTypeChange(option.type);
                  }}
                  className={`p-4 rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 transition-all flex flex-col items-center gap-2 relative group ${
                    option.locked && isDemoMode
                      ? 'border-t-yellow-500/50 border-l-yellow-500/50 border-r-yellow-700/70 border-b-yellow-700/70 bg-gradient-to-b from-yellow-950/40 via-slate-900/30 to-yellow-950/40 backdrop-blur-sm text-yellow-400/70 cursor-pointer hover:scale-105'
                      : settings.type === option.type
                      ? 'border-t-purple-500/60 border-l-purple-500/60 border-r-purple-700/80 border-b-purple-700/80 bg-gradient-to-b from-purple-900/60 via-purple-950/50 to-purple-950/60 backdrop-blur-sm text-purple-300 scale-105'
                      : 'border-t-slate-500/40 border-l-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm text-gray-400 hover:text-white hover:border-purple-500/50 hover:scale-105'
                  }`}
                  style={{
                    boxShadow: option.locked && isDemoMode 
                      ? '0 4px 12px rgba(234, 179, 8, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.1)' 
                      : settings.type === option.type
                      ? '0 4px 12px rgba(168, 85, 247, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                      : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {(option.locked && isDemoMode || settings.type === option.type) && (
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                  )}
                  <span className="text-xl">{option.icon}</span>
                  <span className="text-sm font-medium">{option.label}</span>
                  {option.locked && isDemoMode && (
                    <span className="text-xs text-yellow-400/70 mt-1">🔒 Sign up to unlock</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Preset Backgrounds */}
          {settings.type === 'preset' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">🌍 Select Gate Realm</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(Object.keys(presetBackgrounds) as PresetBackgroundId[]).map((presetId) => {
                  const preset = presetBackgrounds[presetId];
                  const isGradient = preset.url.startsWith('linear-gradient');
                  
                  // In demo mode, only unlock the first preset, lock all others
                  let isLocked: boolean;
                  if (isDemoMode) {
                    const presetKeys = Object.keys(presetBackgrounds) as PresetBackgroundId[];
                    isLocked = presetId !== presetKeys[0]; // Only first preset is unlocked
                  } else {
                    isLocked = preset.requiresSubscription ? 
                      !subscriptionService.hasSubscriptionAccess(userProfile.subscription, preset.tier || 's_rank') : 
                      false;
                  }
                  
                  return (
                    <button
                      key={presetId}
                      onClick={() => {
                        if (isLocked && isDemoMode) {
                          // Show signup prompt for locked presets in demo mode
                          if (onShowSubscription) {
                            onShowSubscription();
                          }
                          return;
                        }
                        handlePresetSelect(presetId);
                      }}
                      className={`relative h-24 rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 overflow-hidden transition-all ${
                        settings.presetId === presetId
                          ? 'border-t-purple-500/60 border-l-purple-500/60 border-r-purple-700/80 border-b-purple-700/80 ring-2 ring-purple-500/50'
                          : isLocked
                          ? 'border-t-slate-700/60 border-l-slate-700/60 border-r-slate-800/70 border-b-slate-800/70 opacity-75'
                          : 'border-t-slate-600/40 border-l-slate-600/40 border-r-slate-700/60 border-b-slate-700/60 hover:border-purple-500/50 hover:scale-105'
                      }`}
                      style={{
                        boxShadow: settings.presetId === presetId 
                          ? '0 4px 12px rgba(168, 85, 247, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                          : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <div
                        className="absolute inset-0"
                        style={
                          isGradient
                            ? { background: preset.url }
                            : { backgroundImage: `url(${preset.url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                        }
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-end p-2">
                        <span className="text-xs font-medium text-white">{preset.name}</span>
                      </div>
                      
                      {/* Lock overlay */}
                      {isLocked && (
                        <div className="absolute inset-0 bg-gradient-to-b from-red-950/80 via-black/70 to-red-950/80 backdrop-blur-sm flex flex-col items-center justify-center"
                          style={{
                            boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.5)'
                          }}
                        >
                          <img 
                            src="/icons/icon-locked.png" 
                            alt="Locked"
                            className="w-8 h-8 object-contain mb-1"
                            style={{ filter: 'drop-shadow(0 0 8px rgba(252, 211, 77, 0.6))' }}
                          />
                          <div className="text-xs font-bold text-yellow-400">
                            {isDemoMode ? 'SIGN UP TO UNLOCK' : (preset.tier === 's_rank' ? 'S-RANK' : 'E-RANK')}
                          </div>
                        </div>
                      )}
                      
                      {/* Selected indicator */}
                      {settings.presetId === presetId && !isLocked && (
                        <div className="absolute top-2 right-2 bg-gradient-to-b from-purple-600/90 to-purple-700 text-white rounded-full p-1.5 shadow-lg shadow-purple-500/50"
                          style={{
                            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.6)'
                          }}
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Image Upload */}
          {settings.type === 'custom_image' && (
            <div className="space-y-6">
              {/* Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">🎭 Summon Custom Gate</label>
                <div className="space-y-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading || (settings.imageCollection || []).length >= 6}
                    className="w-full p-6 border-t-2 border-l-2 border-r-2 border-b-2 border-dashed border-t-slate-600/40 border-l-slate-600/40 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl hover:border-purple-500/50 transition-all flex flex-col items-center gap-2 disabled:opacity-50 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <span className="text-4xl text-gray-400">🌀</span>
                    <span className="text-sm text-gray-400">
                      {isUploading 
                        ? `Manifesting Gate... ${uploadProgress}%` 
                        : (settings.imageCollection || []).length >= 6
                        ? 'Collection Full (6/6)'
                        : `Upload to collection (${(settings.imageCollection || []).length}/6 used)`
                      }
                    </span>
                    <span className="text-xs text-gray-500">JPEG, PNG, or WebP (Max 10MB)</span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {/* My Collections Gallery */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <span className="text-lg">🖼️</span>
                    <span>My Collections</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    {(settings.imageCollection || []).length} / 6 slots
                  </span>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {/* Render existing collection images */}
                  {(settings.imageCollection || []).map((collectionImage, index) => (
                    <div
                      key={`${collectionImage.fileName}-${index}`}
                      className={`relative aspect-video rounded-2xl overflow-hidden border-t-2 border-l-2 border-r-2 border-b-2 transition-all cursor-pointer group ${
                        settings.customImageName === collectionImage.fileName
                          ? 'border-t-purple-500/60 border-l-purple-500/60 border-r-purple-700/80 border-b-purple-700/80 ring-2 ring-purple-500/50'
                          : 'border-t-slate-600/40 border-l-slate-600/40 border-r-slate-700/60 border-b-slate-700/60 hover:border-purple-500/50'
                      }`}
                      style={{
                        boxShadow: settings.customImageName === collectionImage.fileName 
                          ? '0 4px 12px rgba(168, 85, 247, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                          : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                      }}
                      onClick={() => handleApplyCollectionImage(collectionImage)}
                    >
                      <img
                        src={collectionImage.url}
                        alt={`Collection ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-xs font-semibold">Click to apply</span>
                      </div>
                      
                      {/* Active indicator */}
                      {settings.customImageName === collectionImage.fileName && (
                        <div className="absolute top-2 left-2 bg-gradient-to-b from-purple-600/90 to-purple-700 text-white rounded-full p-1.5 shadow-lg shadow-purple-500/50"
                          style={{
                            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.6)'
                          }}
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      
                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCollectionImage(index);
                        }}
                        className="absolute top-2 right-2 bg-gradient-to-b from-red-600/90 to-red-700/90 hover:from-red-700 hover:to-red-800 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all border-t-2 border-l-2 border-red-500/40 border-r-2 border-b-2 border-r-red-700/60 border-b-red-700/60"
                        style={{
                          boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)'
                        }}
                        title="Delete image"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  
                  {/* Render empty slots */}
                  {Array.from({ length: 6 - (settings.imageCollection || []).length }).map((_, index) => (
                    <div
                      key={`empty-${index}`}
                      className="relative aspect-video rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 border-dashed border-t-slate-600/40 border-l-slate-600/40 border-r-slate-700/60 border-b-slate-700/60 flex items-center justify-center bg-gradient-to-b from-slate-800/30 via-slate-900/20 to-slate-950/30 backdrop-blur-sm"
                      style={{
                        boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <div className="text-center">
                        <div className="text-3xl text-gray-600 mb-1">📷</div>
                        <div className="text-xs text-gray-600">Empty slot</div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Current preview */}
                {settings.customImageUrl && (
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-400 mb-2">Currently Applied:</label>
                    <div className="relative h-32 rounded-2xl overflow-hidden border-t-2 border-l-2 border-r-2 border-b-2 border-t-purple-500/50 border-l-purple-500/50 border-r-purple-700/70 border-b-purple-700/70"
                      style={{
                        boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <img
                        src={settings.customImageUrl}
                        alt="Current background"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-2 right-2 bg-gradient-to-b from-purple-600/90 to-purple-700 px-2 py-1 rounded-lg text-xs text-white font-bold backdrop-blur-sm"
                        style={{
                          boxShadow: '0 2px 8px rgba(168, 85, 247, 0.4)'
                        }}
                      >
                        Active
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Gradient Builder */}
          {settings.type === 'gradient' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">💫 Mana Aura Configuration</label>
              <div className="space-y-3">
                {(settings.gradient?.colors || ['#667eea', '#764ba2']).map((color, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => handleGradientColorChange(index, e.target.value)}
                      className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-700"
                    />
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => handleGradientColorChange(index, e.target.value)}
                      className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                      placeholder="#667eea"
                    />
                    {(settings.gradient?.colors || []).length > 2 && (
                      <button
                        onClick={() => handleRemoveGradientColor(index)}
                        className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <span className="text-lg">✕</span>
                      </button>
                    )}
                  </div>
                ))}
                {(settings.gradient?.colors || []).length < 5 && (
                  <button
                    onClick={handleAddGradientColor}
                    className="w-full py-2 border border-dashed border-gray-700 rounded-lg text-gray-400 hover:border-purple-500/50 hover:text-purple-400 transition-colors text-sm"
                  >
                    ➕ Add Mana Layer
                  </button>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={handleRandomGradient}
                    className="flex-1 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg text-purple-400 hover:bg-purple-500/20 transition-colors"
                  >
                    ✨ Random Aura
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Mana Flow Direction: {settings.gradient?.angle || 135}°</label>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={settings.gradient?.angle || 135}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        gradient: {
                          colors: settings.gradient?.colors || ['#667eea', '#764ba2'],
                          angle: parseInt(e.target.value),
                        },
                      })
                    }
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Video Upload */}
          {settings.type === 'video' && (
            <div className="space-y-6">
              {/* Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">🌪️ Summon Living Realm</label>
                <div className="space-y-3">
                  <button
                    onClick={() => videoInputRef.current?.click()}
                    disabled={isUploading || (settings.videoCollection || []).length >= 3}
                    className="w-full p-6 border-t-2 border-l-2 border-r-2 border-b-2 border-dashed border-t-slate-600/40 border-l-slate-600/40 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl hover:border-purple-500/50 transition-all flex flex-col items-center gap-2 disabled:opacity-50 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm"
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <span className="text-4xl text-gray-400">🌊</span>
                    <span className="text-sm text-gray-400">
                      {isUploading 
                        ? `Manifesting realm... ${uploadProgress}%` 
                        : (settings.videoCollection || []).length >= 3
                        ? 'Collection Full (3/3)'
                        : `Upload to collection (${(settings.videoCollection || []).length}/3 used)`
                      }
                    </span>
                    <span className="text-xs text-gray-500">MP4 or WebM (Max 50MB)</span>
                  </button>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/webm"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {/* My Living Realms Gallery */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <span className="text-lg">🎬</span>
                    <span>My Living Realms</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    {(settings.videoCollection || []).length} / 3 slots
                  </span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Render existing collection videos */}
                  {(settings.videoCollection || []).map((collectionVideo, index) => (
                    <div
                      key={`${collectionVideo.fileName}-${index}`}
                      className={`relative aspect-video rounded-2xl overflow-hidden border-t-2 border-l-2 border-r-2 border-b-2 transition-all cursor-pointer group ${
                        settings.videoName === collectionVideo.fileName
                          ? 'border-t-purple-500/60 border-l-purple-500/60 border-r-purple-700/80 border-b-purple-700/80 ring-2 ring-purple-500/50'
                          : 'border-t-slate-600/40 border-l-slate-600/40 border-r-slate-700/60 border-b-slate-700/60 hover:border-purple-500/50'
                      }`}
                      style={{
                        boxShadow: settings.videoName === collectionVideo.fileName 
                          ? '0 4px 12px rgba(168, 85, 247, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                          : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                      }}
                      onClick={() => handleApplyCollectionVideo(collectionVideo)}
                    >
                      <video
                        src={collectionVideo.url}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                      />
                      
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-xs font-semibold">Click to apply</span>
                      </div>
                      
                      {/* Active indicator */}
                      {settings.videoName === collectionVideo.fileName && (
                        <div className="absolute top-2 left-2 bg-gradient-to-b from-purple-600/90 to-purple-700 text-white rounded-full p-1.5 shadow-lg shadow-purple-500/50"
                          style={{
                            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.6)'
                          }}
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      
                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCollectionVideo(index);
                        }}
                        className="absolute top-2 right-2 bg-gradient-to-b from-red-600/90 to-red-700/90 hover:from-red-700 hover:to-red-800 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all border-t-2 border-l-2 border-red-500/40 border-r-2 border-b-2 border-r-red-700/60 border-b-red-700/60"
                        style={{
                          boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)'
                        }}
                        title="Delete video"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  
                  {/* Render empty slots */}
                  {Array.from({ length: 3 - (settings.videoCollection || []).length }).map((_, index) => (
                    <div
                      key={`empty-${index}`}
                      className="relative aspect-video rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 border-dashed border-t-slate-600/40 border-l-slate-600/40 border-r-slate-700/60 border-b-slate-700/60 flex items-center justify-center bg-gradient-to-b from-slate-800/30 via-slate-900/20 to-slate-950/30 backdrop-blur-sm"
                      style={{
                        boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <div className="text-center">
                        <div className="text-3xl text-gray-600 mb-1">🎥</div>
                        <div className="text-xs text-gray-600">Empty slot</div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Current preview */}
                {settings.videoUrl && (
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-400 mb-2">Currently Applied:</label>
                    <div className="relative h-32 rounded-2xl overflow-hidden border-t-2 border-l-2 border-r-2 border-b-2 border-t-purple-500/50 border-l-purple-500/50 border-r-purple-700/70 border-b-purple-700/70"
                      style={{
                        boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <video
                        src={settings.videoUrl}
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                      <div className="absolute bottom-2 right-2 bg-gradient-to-b from-purple-600/90 to-purple-700 px-2 py-1 rounded-lg text-xs text-white font-bold backdrop-blur-sm"
                        style={{
                          boxShadow: '0 2px 8px rgba(168, 85, 247, 0.4)'
                        }}
                      >
                        Active
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Solid Color */}
          {settings.type === 'solid' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">⬛ Void Configuration</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.solidColor || '#1a1a2e'}
                  onChange={(e) => setSettings({ ...settings, solidColor: e.target.value })}
                  className="w-16 h-16 rounded-lg cursor-pointer border-2 border-gray-700"
                />
                <input
                  type="text"
                  value={settings.solidColor || '#1a1a2e'}
                  onChange={(e) => setSettings({ ...settings, solidColor: e.target.value })}
                  className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  placeholder="#1a1a2e"
                />
              </div>
            </div>
          )}

          {/* Overlay Opacity */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              🌑 Shadow Veil Intensity: {settings.overlayOpacity}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.overlayOpacity}
              onChange={(e) => setSettings({ ...settings, overlayOpacity: parseInt(e.target.value) })}
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">Darken realm for enhanced stat visibility</p>
          </div>

          {/* Blur Intensity */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              💨 Mist Effect: {settings.blurIntensity}px
            </label>
            <input
              type="range"
              min="0"
              max="20"
              value={settings.blurIntensity}
              onChange={(e) => setSettings({ ...settings, blurIntensity: parseInt(e.target.value) })}
              className="w-full"
            />
          </div>

          {/* Particle Effects */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <span className="text-lg">✨</span>
              Atmospheric Mana Particles
            </label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {particleEffects.map((effect) => (
                <button
                  key={effect.type}
                  onClick={() => {
                    setSettings({ ...settings, particleEffect: effect.type });
                    soundService.buttonClick();
                    hapticService.click();
                  }}
                  className={`p-3 rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 transition-all flex flex-col items-center gap-1 relative ${
                    settings.particleEffect === effect.type
                      ? 'border-t-purple-500/60 border-l-purple-500/60 border-r-purple-700/80 border-b-purple-700/80 bg-gradient-to-b from-purple-900/60 via-purple-950/50 to-purple-950/60 backdrop-blur-sm scale-105'
                      : 'border-t-slate-500/40 border-l-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm hover:border-purple-500/50 hover:scale-105'
                  }`}
                  style={{
                    boxShadow: settings.particleEffect === effect.type 
                      ? '0 4px 12px rgba(168, 85, 247, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                      : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {settings.particleEffect === effect.type && (
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                  )}
                  <span className="text-2xl">{effect.icon}</span>
                  <span className="text-xs text-gray-400">{effect.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Additional Options */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg cursor-pointer hover:bg-gray-800 transition-colors">
              <input
                type="checkbox"
                checked={settings.parallaxEnabled}
                onChange={(e) => setSettings({ ...settings, parallaxEnabled: e.target.checked })}
                className="w-5 h-5 rounded border-gray-700"
              />
              <div>
                <span className="text-sm font-medium text-gray-300">🌀 Dimensional Depth Effect</span>
                <p className="text-xs text-gray-500">Realm shifts with your movement</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg cursor-pointer hover:bg-gray-800 transition-colors">
              <input
                type="checkbox"
                checked={settings.reducedQuality}
                onChange={(e) => setSettings({ ...settings, reducedQuality: e.target.checked })}
                className="w-5 h-5 rounded border-gray-700"
              />
              <div>
                <span className="text-sm font-medium text-gray-300">⚡ Low Mana Mode</span>
                <p className="text-xs text-gray-500">Conserve system resources for smoother performance</p>
              </div>
            </label>
          </div>
        </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-t border-purple-500/20 p-6 flex gap-3"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            <button
              onClick={onClose}
              className="flex-1 py-3 px-6 bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 hover:from-slate-600/70 hover:via-slate-700/70 hover:to-slate-800/70 text-white rounded-2xl transition-all font-medium border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70 hover:scale-105 active:scale-95"
              style={{
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
              }}
            >
              Dispel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-3 px-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-2xl transition-all disabled:opacity-50 font-medium relative overflow-hidden hover:scale-105 active:scale-95"
              style={{
                boxShadow: '0 4px 12px rgba(147, 51, 234, 0.4), 0 1px 4px rgba(219, 39, 119, 0.2), inset 0 1px 2px rgba(255, 100, 255, 0.2), inset 0 -1px 2px rgba(0, 0, 0, 0.2)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
              <span className="relative">{isSaving ? 'Manifesting Realm...' : 'Manifest & Apply'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundCustomizer;

